import {useCallback, useEffect, useState} from 'react';
import type {RingTargetState} from '@phonehq/rtc-core';
import {isRinging} from './invitees';

export const ALONE_AUTO_LEAVE_MS = 120_000;
const TICK_MS = 1_000;

export interface AloneAutoLeave {
    readonly remainingMs: number;
    readonly stay: () => void;
}

export const aloneWithoutAnswer = (alone: boolean, waiting: readonly RingTargetState[]): boolean =>
    alone && waiting.length > 0 && !waiting.some(isRinging);

export const useAloneAutoLeave = (alone: boolean, waiting: readonly RingTargetState[], leave: () => void): AloneAutoLeave | undefined => {
    const armed = aloneWithoutAnswer(alone, waiting);
    const [deadline, setDeadline] = useState<number>();
    const [now, setNow] = useState(Date.now);

    useEffect(() => {
        setNow(Date.now());
        setDeadline(armed ? Date.now() + ALONE_AUTO_LEAVE_MS : undefined);
    }, [armed]);

    useEffect(() => {
        if (deadline === undefined) {
            return;
        }
        const timer = window.setInterval(() => setNow(Date.now()), TICK_MS);
        return () => window.clearInterval(timer);
    }, [deadline]);

    useEffect(() => {
        if (deadline !== undefined && now >= deadline) {
            setDeadline(undefined);
            leave();
        }
    }, [deadline, leave, now]);

    const stay = useCallback(() => {
        setNow(Date.now());
        setDeadline(Date.now() + ALONE_AUTO_LEAVE_MS);
    }, []);

    return deadline === undefined ? undefined : {remainingMs: Math.max(0, deadline - now), stay};
};
